import { useCallback, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import { SERASA_ADMIN, SYSTEM_VARIABLES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";

const CASE_14_CONTRACTS_CONFIG_KEY = "CASE_14_CONTRACTS_CONFIG";

// ─── helpers ──────────────────────────────────────────────────────────────────

const fetchVariable = async (key) => {
  try {
    const { data } = await axiosInstance.get(
      `${SYSTEM_VARIABLES.GET_BY_KEY}/${encodeURIComponent(key)}`,
    );
    return data?.data?.value ?? null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

const saveVariable = (key, value, description) =>
  axiosInstance.post(SYSTEM_VARIABLES.UPDATE, { key, value, description });

const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const barColor = (requestsToday, maxRequestsPerDay) => {
  if (!maxRequestsPerDay || maxRequestsPerDay <= 0) return "#6366f1";
  const pct = requestsToday / maxRequestsPerDay;
  if (pct >= 0.9) return "#ef4444";
  if (pct >= 0.7) return "#f59e0b";
  return "#10b981";
};

// ─── chart builder ────────────────────────────────────────────────────────────

const buildChartConfig = (stats) => {
  const labels = stats.contracts.map((_, i) => `Contrato ${i + 1}`);
  const values = stats.contracts.map((c) => c.requestsToday);
  const colors = stats.contracts.map((c) =>
    barColor(c.requestsToday, c.maxRequestsPerDay),
  );

  return {
    series: [{ name: "Requisições hoje", data: values }],
    options: {
      chart: {
        type: "bar",
        height: Math.max(120, stats.contracts.length * 60),
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          distributed: true,
          dataLabels: { position: "center" },
          barHeight: "55%",
        },
      },
      colors,
      dataLabels: {
        enabled: true,
        style: { fontSize: "12px", fontWeight: 600, colors: ["#fff"] },
        formatter: (val, { dataPointIndex }) => {
          const c = stats.contracts[dataPointIndex];
          if (c.maxRequestsPerDay > 0) {
            const pct = Math.round((val / c.maxRequestsPerDay) * 100);
            return `${val} / ${c.maxRequestsPerDay}  (${pct}%)`;
          }
          return val === 0 ? "0" : `${val}  (sem limite)`;
        },
      },
      xaxis: {
        categories: labels,
        labels: { style: { fontSize: "13px", fontWeight: 500 } },
      },
      yaxis: {
        labels: { style: { fontSize: "13px", fontWeight: 600 } },
      },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val, { dataPointIndex }) => {
            const c = stats.contracts[dataPointIndex];
            if (c.maxRequestsPerDay > 0) {
              const pct = Math.round((val / c.maxRequestsPerDay) * 100);
              return `${val} de ${c.maxRequestsPerDay} (${pct}%)`;
            }
            return `${val} (sem limite diário)`;
          },
        },
      },
    },
  };
};

// ─── component ────────────────────────────────────────────────────────────────

export default function SerasaSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ── load contracts ──────────────────────────────────────────────────────────

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const raw = await fetchVariable(CASE_14_CONTRACTS_CONFIG_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setContracts(
              parsed.map((c) => ({
                clientId: c.clientId ?? "",
                clientSecret: c.clientSecret ?? "",
                maxRequestsPerDay: c.maxRequestsPerDay ?? 0,
              })),
            );
            return;
          }
        } catch {}
      }
      setContracts([{ clientId: "", clientSecret: "", maxRequestsPerDay: 0 }]);
    } catch {
      setMessage({ type: "error", text: "Erro ao carregar configurações." });
    } finally {
      setLoading(false);
    }
  };

  // ── load stats ──────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await axiosInstance.get(SERASA_ADMIN.GET_STATS);
      setStats(data?.data ?? null);
    } catch {
      // silently ignore — stats are non-critical
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadStats();
  }, [loadStats]);

  // ── contract mutations ──────────────────────────────────────────────────────

  const updateContract = (index, field, value) => {
    setContracts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  };

  const addContract = () => {
    setContracts((prev) => [
      ...prev,
      { clientId: "", clientSecret: "", maxRequestsPerDay: 0 },
    ]);
  };

  const removeContract = (index) => {
    setContracts((prev) => prev.filter((_, i) => i !== index));
  };

  // ── save ────────────────────────────────────────────────────────────────────

  const save = async () => {
    if (contracts.length === 0) {
      setMessage({ type: "error", text: "Adicione ao menos um contrato." });
      return;
    }

    const hasEmpty = contracts.some(
      (c) => !c.clientId.trim() || !c.clientSecret.trim(),
    );
    if (hasEmpty) {
      setMessage({
        type: "error",
        text: "Preencha Client ID e Client Secret em todos os contratos.",
      });
      return;
    }

    const clientIds = contracts.map((c) => c.clientId.trim());
    if (new Set(clientIds).size !== clientIds.length) {
      setMessage({
        type: "error",
        text: "Existem Client IDs duplicados.",
      });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const payload = contracts.map((c) => ({
        clientId: c.clientId.trim(),
        clientSecret: c.clientSecret.trim(),
        maxRequestsPerDay: Math.max(0, Number(c.maxRequestsPerDay) || 0),
      }));

      await saveVariable(
        CASE_14_CONTRACTS_CONFIG_KEY,
        JSON.stringify(payload),
        "Contratos Serasa para o case 14.",
      );

      setMessage({ type: "success", text: "Contratos salvos com sucesso." });
      await Promise.all([loadConfig(), loadStats()]);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || "Erro ao salvar contratos.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="xl" />
      </div>
    );
  }

  const chart = stats && stats.contracts.length > 0 ? buildChartConfig(stats) : null;

  return (
    <div className="space-y-6">
      {/* ── contracts card ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* header */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Contratos Serasa</h2>
              <p className="text-xs text-gray-500">
                Distribuição aleatória e alternada — respeita o limite diário de cada contrato (0 = ilimitado)
              </p>
            </div>
            <span className="ml-auto rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-semibold text-indigo-700">
              {contracts.length} {contracts.length === 1 ? "contrato" : "contratos"}
            </span>
          </div>
        </div>

        {/* contract list */}
        <div className="divide-y divide-gray-50 px-6 py-4">
          {contracts.map((contract, index) => (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                    {index + 1}
                  </span>
                  Contrato {index + 1}
                </span>
                {contracts.length > 1 && (
                  <button
                    onClick={() => removeContract(index)}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    Remover
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <Label
                    htmlFor={`cid-${index}`}
                    value="Client ID"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <TextInput
                    id={`cid-${index}`}
                    value={contract.clientId}
                    onChange={(e) => updateContract(index, "clientId", e.target.value)}
                    placeholder="client_id"
                    sizing="sm"
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`cs-${index}`}
                    value="Client Secret"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <TextInput
                    id={`cs-${index}`}
                    type="password"
                    value={contract.clientSecret}
                    onChange={(e) => updateContract(index, "clientSecret", e.target.value)}
                    placeholder="client_secret"
                    sizing="sm"
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`max-${index}`}
                    value="Máx. req/dia"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  />
                  <div className="relative">
                    <TextInput
                      id={`max-${index}`}
                      type="number"
                      min={0}
                      value={String(contract.maxRequestsPerDay)}
                      onChange={(e) =>
                        updateContract(index, "maxRequestsPerDay", e.target.value)
                      }
                      placeholder="0"
                      sizing="sm"
                    />
                    {Number(contract.maxRequestsPerDay) === 0 && (
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400">
                        ∞
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            onClick={addContract}
            className="flex items-center gap-1.5 rounded-lg border border-dashed border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar contrato
          </button>

          <div className="flex items-center gap-3">
            {message.text && (
              <span
                className={`text-sm font-medium ${
                  message.type === "error" ? "text-red-600" : "text-emerald-600"
                }`}
              >
                {message.text}
              </span>
            )}
            <Button onClick={save} isProcessing={saving} size="sm">
              Salvar contratos
            </Button>
          </div>
        </div>
      </div>

      {/* ── stats card ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Consumo de Hoje</h2>
              {stats && (
                <p className="text-xs text-gray-500">{formatDate(stats.date)}</p>
              )}
            </div>
          </div>

          <button
            onClick={loadStats}
            disabled={loadingStats}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg
              className={`h-3.5 w-3.5 ${loadingStats ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        </div>

        {/* chart body */}
        <div className="px-6 py-5">
          {loadingStats && !stats ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : !chart ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <svg className="mb-3 h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-sm text-gray-400">
                Nenhuma requisição registrada hoje ou nenhum contrato configurado.
              </p>
            </div>
          ) : (
            <>
              <ReactApexChart
                key={stats.date}
                options={chart.options}
                series={chart.series}
                type="bar"
                height={Math.max(140, stats.contracts.length * 64)}
              />

              {/* legend */}
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
                  Abaixo de 70%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" />
                  70% – 90%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" />
                  Acima de 90%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-500" />
                  Sem limite
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
